import type { BabyEvent, ChildDto, DayStat, DaySummary, FamilyDto, FamilySettings, MeDto, MemberDto, ReminderSettingsDto, Role, UserSettings } from '@bb/shared/core';
import { platform } from './platform';

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method,
      headers: { authorization: platform.authHeader(), ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network', 'Нет связи с сервером. Проверьте интернет.');
  }
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
  if (!res.ok) throw new ApiError(res.status, data.error ?? 'error', data.message ?? 'Что-то пошло не так');
  return data as T;
}

export interface DayDto {
  date: string;
  tz: string;
  events: BabyEvent[];
  summary: DaySummary;
}

export interface MeasurePoint {
  date: string;
  weightG?: number;
  heightCm?: number;
  headCm?: number;
}

export interface StatsDto {
  from: string;
  to: string;
  today: string;
  tz: string;
  days: DayStat[];
  measures: MeasurePoint[];
  health: BabyEvent[];
}

export type NewChild = Omit<ChildDto, 'id' | 'familyId' | 'photoUrl'>;

export interface RecentDto {
  now: number;
  tz: string;
  /** Записи за последние 48 часов и идущие таймеры. */
  events: BabyEvent[];
  /** Последняя запись каждого вида (грудь, бутылочка, подгузник, сон) — даже старше окна. */
  last: BabyEvent[];
}

export interface NewEvent {
  type: BabyEvent['type'];
  startedAt: string;
  endedAt?: string | null;
  payload: Record<string, unknown>;
  note?: string;
}

export const api = {
  me: () => request<MeDto & { startParam: string | null }>('GET', '/me'),
  createFamily: (body: { child: Omit<ChildDto, 'id' | 'familyId' | 'photoUrl'>; tz: string; displayName?: string }) =>
    request<FamilyDto>('POST', '/families', body),
  recent: (childId: string) => request<RecentDto>('GET', `/children/${childId}/recent`),
  setPhoto: (childId: string, dataUrl: string | null) => request<ChildDto>('PUT', `/children/${childId}/photo`, { dataUrl }),
  saveUserSettings: (s: UserSettings) => request<UserSettings>('PUT', '/me/settings', s),
  saveFamilySettings: (familyId: string, s: FamilySettings) => request<FamilySettings>('PUT', `/families/${familyId}/settings`, s),
  addChild: (familyId: string, c: NewChild) => request<ChildDto>('POST', `/families/${familyId}/children`, c),
  deleteChild: (id: string) => request<void>('DELETE', `/children/${id}`),
  stats: (childId: string, from: string, to: string) => request<StatsDto>('GET', `/children/${childId}/stats?from=${from}&to=${to}`),
  updateChild: (id: string, patch: Partial<Omit<ChildDto, 'id' | 'familyId' | 'photoUrl'>>) => request<ChildDto>('PATCH', `/children/${id}`, patch),
  members: (familyId: string) => request<MemberDto[]>('GET', `/families/${familyId}/members`),
  updateMember: (familyId: string, userId: number, patch: { role?: Role; displayName?: string; childIds?: string[] | null }) =>
    request<void>('PATCH', `/families/${familyId}/members/${userId}`, patch),
  removeMember: (familyId: string, userId: number) => request<void>('DELETE', `/families/${familyId}/members/${userId}`),
  /** childIds: null — на всех малышей семьи (и будущих), иначе — только на выбранных. */
  invite: (familyId: string, role: Role, childIds: string[] | null = null) =>
    request<{ token: string; link: string | null; expiresAt: string }>('POST', `/families/${familyId}/invites`, { role, childIds }),
  acceptInvite: (token: string) => request<{ familyId: string; role: Role; joined: boolean }>('POST', `/invites/${token}/accept`),
  reminders: (familyId: string) => request<ReminderSettingsDto>('GET', `/families/${familyId}/reminders`),
  saveReminders: (familyId: string, s: ReminderSettingsDto) => request<ReminderSettingsDto>('PUT', `/families/${familyId}/reminders`, s),
  day: (childId: string, date?: string) => request<DayDto>('GET', `/children/${childId}/day${date ? `?date=${date}` : ''}`),
  createEvent: (childId: string, e: NewEvent) => request<BabyEvent>('POST', `/children/${childId}/events`, e),
  updateEvent: (id: string, patch: Partial<Omit<NewEvent, 'type' | 'note'>> & { note?: string | null }) => request<BabyEvent>('PATCH', `/events/${id}`, patch),
  stopEvent: (id: string) => request<BabyEvent>('POST', `/events/${id}/stop`, {}),
  deleteEvent: (id: string) => request<void>('DELETE', `/events/${id}`),
  restoreEvent: (id: string) => request<BabyEvent>('POST', `/events/${id}/restore`),
};
